import {Button} from "primereact/button";
import {Message} from "primereact/message";
import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";

import {MimeType} from "@reva/shared/mime-type";

import type {AvmDocumentedEndpoint} from "../api/api-constants-avm";
import {WebError} from "../api/web-error";
import type {FetchDocument} from "../component/AvmDocumentationLink";
import {useRevaTranslation} from "../hooks/languageHooks";
import {useCheckPermission, useCreatablePurposes} from "../hooks/permissionHooks";
import logoLight from "../images/logo_value_marktdaten.png";
import logoDark from "../images/logo_value_marktdaten_dark.png";
import {
    fetchAvmDocumentation,
    fetchAvmDocumentationList,
    fetchAvmParameters,
    fetchAvmReleaseNotes,
    fetchAvmValidationReport,
    fetchIndicationDocumentation,
    fetchMicroMacroDocumentation,
    fetchRevaManual,
    fetchTimelinesDocumentation,
} from "../redux/pdfThunk";
import {useRevaDispatch, useRevaSelector} from "../redux/revaHooks";
import {selectDarkTheme} from "../redux/themeSlice";
import {navigateHard, openBlobForDownload} from "../util/browserUtil";
import {formatDate} from "../util/dateUtil";
import {PERMISSION_RULE_MARKET_TIMELINES, type PermissionRule} from "../util/permissions";
import {
    type AvmDocumentationEntry,
    type AvmDocumentationKey,
    documentationUpdatedAt,
    parseDocumentationList,
    REVA_MANUALS,
    resolveManualPurpose,
} from "../util/revaManuals";
import {RevaPermission} from "../util/revaPermission";

type DownloadState = "ready" | "loading" | "done" | "error" | "forbidden";

/** Mindesthöhe der Knöpfe, damit sie auf dem Handy gut zu treffen sind. */
const TOUCH_TARGET = {minHeight: "44px"};

/**
 * Lädt ein PDF per Klick und bietet es zum Download an, mit eigenem Zustand je Knopf.
 *
 * Mobile Browser unterdrücken Downloads ohne Nutzergeste oft still. Das geladene PDF bleibt daher im State, damit ein
 * weiterer Klick es synchron – noch innerhalb der Nutzergeste – anbietet; nach dem `await` des ersten Ladens kann die
 * Geste bereits abgelaufen sein.
 * @param fetchDocument Thunk-Factory, die das PDF über den REVA-Proxy holt
 * @param fallbackFilename Dateiname, falls die AVM-API keinen via `Content-Disposition` mitschickt
 * @param cacheKey Schlüssel des geladenen PDFs; ändert er sich (z. B. Zweck/Sprache), wird neu geladen
 */
function useManualDownload(fetchDocument: FetchDocument, fallbackFilename: string, cacheKey = "") {
    const dispatch = useRevaDispatch();
    const [state, setState] = useState<DownloadState>("ready");
    const [cached, setCached] = useState<{key: string; file: File} | null>(null);

    const offer = (file: File) => {
        openBlobForDownload(file, file.name);
        setState("done");
    };

    const download = async () => {
        if (cached?.key === cacheKey) {
            offer(cached.file);
            return;
        }
        setState("loading");
        try {
            const {blob, filename} = await dispatch(fetchDocument());
            const file = new File([blob], filename ?? fallbackFilename, {type: MimeType.PDF});
            setCached({key: cacheKey, file});
            offer(file);
        } catch (e) {
            console.error(`Failed to download ${fallbackFilename}`, e);
            setState(e instanceof WebError && e.status === 403 ? "forbidden" : "error");
        }
    };

    return {state, download};
}

/**
 * Lädt einmal pro Seitenaufbau die Dokumentationsliste der AVM-API für die Stand-Daten (#19302). Die Knöpfe hängen
 * nicht daran; bei 401/403/404 (AVM-API ohne den Endpunkt), Netzfehler oder unerwartetem Format bleibt die Liste
 * leer, ohne Meldung.
 */
function useDocumentationList(): AvmDocumentationEntry[] {
    const dispatch = useRevaDispatch();
    const [entries, setEntries] = useState<AvmDocumentationEntry[]>([]);

    useEffect(() => {
        let active = true;
        dispatch(fetchAvmDocumentationList())
            .then((json: unknown) => {
                if (active) setEntries(parseDocumentationList(json));
            })
            .catch((e: unknown) => console.debug("Dokumentationsliste nicht verfügbar, Stand-Daten entfallen", e));
        return () => {
            active = false;
        };
    }, [dispatch]);

    return entries;
}

/**
 * Dezente Zeile „Stand: …“ unter einem Knopf; ohne Datum nichts.
 * @param props `id` der Zeile und Upload-Datum (ISO-8601) oder null
 */
function UpdatedAtLine({id, updatedAt}: {id: string; updatedAt: string | null}) {
    const {t, language} = useRevaTranslation();
    const date = formatDate(updatedAt, language);
    if (!date) return null;
    return (
        <div id={id} className="text-sm text-muted-color">
            {t("manual.updatedAt", {date})}
        </div>
    );
}

/** Ein weiteres Dokument unter den Hauptknöpfen der Handbuch-Seite. */
interface ManualDocument {
    /** Suffix der Element-ID (`manualDoc…`). */
    id: string;
    /** Beschriftung, dieselbe wie an den übrigen Stellen in REVA. */
    label: string;
    fetchDocument: FetchDocument;
    fallbackFilename: string;
    /** Schlüssel des geladenen PDFs, z. B. die Sprache; ändert er sich, wird neu geladen. */
    cacheKey?: string;
    /** Recht aus der Anmeldeantwort; ohne das Recht antwortet die AVM-API mit 403, der Knopf bleibt dann weg. */
    permission?: PermissionRule;
    /** Zeile der Dokumentationsliste mit dem Stand-Datum. */
    listKey: AvmDocumentationKey;
}

/** Recht je Methodenbeschreibung – dieselbe Lizenzrolle, mit der die AVM-API das PDF ausliefert. */
const METHOD_DOCUMENTATION_PERMISSIONS: Record<AvmDocumentedEndpoint, RevaPermission> = {
    VALUE: RevaPermission.INDICATION_VALUE,
    COMPARATIVE_PRICE: RevaPermission.INDICATION_COMPARATIVE_PRICE,
    COMPARATIVE_VALUE: RevaPermission.INDICATION_COMPARATIVE_VALUE,
    REAL_ASSET_VALUE: RevaPermission.INDICATION_REALASSET_VALUE,
    INCOME_VALUE: RevaPermission.INDICATION_INCOME_VALUE,
    ENERGY_VALUE: RevaPermission.INDICATION_ENERGY_VALUE,
};

/**
 * Weitere Dokumente der Seite (#19302): AVM-Dokumentation und -Releasenotes wie im Fußzeilen-Menü „Dokumentation“,
 * Parameterübersicht und Validierungsbericht der AVM-API, die Methodenbeschreibungen der Verfahren wie im
 * Info-Bereich der Wert-Seite, die der Wohnlagen und die Zeitreihen-Dokumentation wie auf der Markt-Seite.
 * Feste Liste aus dem Code: die Seite lädt bewusst keine Spec.
 *
 * Parameterübersicht und Validierungsbericht liefert die AVM-API ohne Rechteprüfung aus, sie stehen daher wie
 * AVM-Dokumentation und Releasenotes ohne Recht in der Liste.
 * @param t Übersetzungsfunktion
 * @param language Oberflächensprache; die Parameterübersicht gibt es auf Deutsch und Englisch
 */
function manualDocuments(
    t: (key: string, options?: Record<string, unknown>) => string,
    language: string,
): ManualDocument[] {
    // Die AVM-API nimmt ohne `language` Englisch, daher immer explizit und nur `de`/`en`.
    const parametersLanguage = language.toLowerCase().startsWith("en") ? "en" : "de";
    const methods = Object.entries(METHOD_DOCUMENTATION_PERMISSIONS).map(([endpoint, permission]) => ({
        id: `Method-${endpoint}`,
        label: t("value.info.methodDocu", {method: t(`value.info.methodName.${endpoint}`)}),
        fetchDocument: () => fetchIndicationDocumentation(endpoint as AvmDocumentedEndpoint),
        fallbackFilename: `VALUE_${endpoint}_methodenbeschreibung.pdf`,
        permission,
        listKey: {type: "documentation" as const, endpoint},
    }));
    return [
        {
            id: "AvmDocumentation",
            label: t("value.info.docu"),
            fetchDocument: fetchAvmDocumentation,
            fallbackFilename: "VALUE_AVM_dokumentation.pdf",
            listKey: {type: "avm_documentation"},
        },
        {
            id: "AvmReleaseNotes",
            label: t("value.info.relasenotes"),
            fetchDocument: fetchAvmReleaseNotes,
            fallbackFilename: "VALUE_AVM_releasenotes.pdf",
            listKey: {type: "release"},
        },
        {
            id: "AvmParameters",
            label: t("manual.avmParameters"),
            fetchDocument: () => fetchAvmParameters(parametersLanguage),
            fallbackFilename:
                parametersLanguage === "en"
                    ? "VALUE_Automated_valuation_models_parameter_overview.pdf"
                    : "VALUE_Automated_valuation_models_parameteruebersicht.pdf",
            cacheKey: parametersLanguage,
            listKey: {type: "parameters", language: parametersLanguage},
        },
        {
            id: "AvmValidationReport",
            label: t("manual.avmValidationReport"),
            fetchDocument: fetchAvmValidationReport,
            fallbackFilename: "VALUE_AVM_validierungsbericht.pdf",
            listKey: {type: "validation_report"},
        },
        ...methods,
        {
            id: "ResidentialLocations",
            label: t("value.info.methodDocu", {method: t("manual.methodNameResidentialLocations")}),
            fetchDocument: fetchMicroMacroDocumentation,
            fallbackFilename: "AVM_Wohnlagen_methodenbeschreibung.pdf",
            permission: RevaPermission.LOCATION_MICROMACRO,
            listKey: {type: "documentation", endpoint: "LOCATION_INFORMATION_MICRO_MACRO"},
        },
        {
            id: "Timelines",
            label: t("market.timelines.documentation_download"),
            fetchDocument: fetchTimelinesDocumentation,
            fallbackFilename: "REVA-Zeitreihen-Dokumentation.pdf",
            permission: PERMISSION_RULE_MARKET_TIMELINES,
            listKey: {type: "documentation", endpoint: "LOCATION_INFORMATION_TIMELINES"},
        },
    ];
}

/**
 * Knopf für ein weiteres Dokument samt Stand-Datum und eigener Statuszeile; ohne das nötige Recht nichts.
 * @param props das Dokument und sein Upload-Datum (ISO-8601) oder null
 */
function ManualDocumentButton({document, updatedAt}: {document: ManualDocument; updatedAt: string | null}) {
    const {t} = useTranslation();
    const allowed = useCheckPermission(document.permission);
    const {state, download} = useManualDownload(document.fetchDocument, document.fallbackFilename, document.cacheKey);

    if (!allowed) return null;

    const id = `manualDoc${document.id}`;
    const loading = state === "loading";
    return (
        <li className="flex flex-col gap-1">
            <Button
                id={id}
                type="button"
                className="w-full text-left"
                style={TOUCH_TARGET}
                icon="pi pi-file-pdf"
                label={document.label}
                severity="secondary"
                outlined
                loading={loading}
                disabled={loading}
                onClick={() => void download()}
            />
            <UpdatedAtLine id={`${id}UpdatedAt`} updatedAt={updatedAt} />
            <div id={`${id}Status`} aria-live="polite" className="text-sm">
                {loading && <span>{t("manual.documentLoading")}</span>}
                {state === "done" && <span>{t("manual.documentStarted")}</span>}
                {state === "error" && <Message className="w-full" severity="error" text={t("manual.documentError")} />}
                {state === "forbidden" && (
                    <Message className="w-full" severity="warn" text={t("manual.documentForbidden")} />
                )}
            </div>
        </li>
    );
}

/**
 * Schlanke Handbuch-Seite unter `/handbuch` (#19302), Ziel des QR-Codes im Handbuch-PDF und daher meist am Handy
 * geöffnet: Logo, die Hauptknöpfe mit dem Namen des Handbuchs (z. B. „Anwenderhandbuch Bewertung“, wie im
 * Fußzeilen-Menü) und „REVA starten“, darunter die weiteren Dokumentationen.
 *
 * `AppContainer` zeigt sie direkt nach der Anmeldung, ohne Router, Kopfzeile, Seitenleiste und Fußzeile; `loginThunk`
 * lädt hier keine statischen Daten (Spec, GeoServer, Freischaltungen). Die Downloads brauchen nur das Token und laufen
 * erst per Klick. „REVA starten“ navigiert deshalb hart auf `/`, damit der normale Ladeweg samt statischer Daten läuft.
 *
 * Ein direkter Link auf die AVM-API taugt nicht als QR-Ziel: das JWT liegt im `localStorage`, nicht in einem Cookie.
 * Ohne Anmeldung zeigt `AppContainer` an derselben URL das Login-Formular.
 *
 * Die Knöpfe stehen sofort aus der festen Liste im Code; danach holt genau ein Request die Dokumentationsliste der
 * AVM-API und ergänzt je Knopf das Stand-Datum (Upload-Datum), siehe {@link useDocumentationList}.
 *
 * Der Zweck kommt aus dem Query-Parameter `purpose` (siehe {@link resolveManualPurpose}; Rechte nur aus der
 * Anmeldeantwort), die Sprache aus der Oberfläche.
 */
export default function PageManual() {
    const {t, language} = useRevaTranslation();
    const isDarkTheme = useRevaSelector(selectDarkTheme);
    const purposeParam = new URLSearchParams(window.location.search).get("purpose");
    const creatable = useCreatablePurposes();
    const purpose = resolveManualPurpose(purposeParam, creatable);
    const manual = REVA_MANUALS[purpose];
    const {state, download} = useManualDownload(
        () => fetchRevaManual(purpose, language),
        manual?.fallbackFilename ?? "REVA_handbuch.pdf",
        `${purpose}|${language}`,
    );
    const documentationList = useDocumentationList();

    const loading = state === "loading";
    const failed = state === "error" || state === "forbidden";

    return (
        <main id="manualPage" className="flex justify-center w-full box-border px-4 py-8">
            <div className="flex flex-col gap-4 w-full max-w-md min-w-0">
                <img
                    id="manualLogo"
                    src={isDarkTheme ? logoDark : logoLight}
                    className="block mx-auto mb-4 w-3/5 h-auto"
                    style={{maxWidth: "240px"}}
                    alt="Value Marktdaten"
                />
                <div className="flex flex-col gap-1">
                    <Button
                        id="manualDownload"
                        type="button"
                        className="w-full"
                        style={TOUCH_TARGET}
                        icon="pi pi-download"
                        label={failed ? t("manual.retry") : t(manual?.labelKey ?? "footer.manualValuation")}
                        loading={loading}
                        disabled={loading}
                        onClick={() => void download()}
                    />
                    <UpdatedAtLine
                        id="manualDownloadUpdatedAt"
                        updatedAt={documentationUpdatedAt(
                            documentationList,
                            {type: manual?.documentationType ?? "reva_manual_valuation"},
                            language,
                        )}
                    />
                </div>
                <Button
                    id="manualStart"
                    type="button"
                    className="w-full"
                    style={TOUCH_TARGET}
                    label={t("manual.start")}
                    severity="secondary"
                    outlined
                    onClick={() => navigateHard("/")}
                />
                <div aria-live="polite">
                    {loading && <span id="manualLoading">{t("manual.loading")}</span>}
                    {state === "done" && (
                        <Message id="manualStarted" className="w-full" severity="success" text={t("manual.started")} />
                    )}
                    {failed && (
                        <Message id="manualError" className="w-full" severity="error" text={t("manual.error")} />
                    )}
                </div>
                <section id="manualDocuments" aria-labelledby="manualDocumentsTitle" className="mt-4">
                    <h2 id="manualDocumentsTitle" className="text-lg font-semibold m-0 mb-3">
                        {t("manual.moreDocumentation")}
                    </h2>
                    <ul className="flex flex-col gap-3 list-none m-0 p-0">
                        {manualDocuments(t, language).map((document) => (
                            <ManualDocumentButton
                                key={document.id}
                                document={document}
                                updatedAt={documentationUpdatedAt(documentationList, document.listKey, language)}
                            />
                        ))}
                    </ul>
                </section>
            </div>
        </main>
    );
}
